import {create} from "zustand"
import {
    Employee,
    WorkdayStatusEnum as StatusEnum,
    WorkdayWithEmployee,
    WorkEventInline,
} from "@src/httpClient/__generated__/Api.ts";
import {WorkDay} from "@src/db.ts";
import {CommonEventsList} from "@src/Store/eventsStore.ts";

/**
 * Workday fields with inline events, assembled client-side.
 */
export interface WorkDayWithEvents {
    id: number
    date: string
    start_time: string | null
    end_time: string | null
    status: StatusEnum
    total_time: string | null
    inconsistent: boolean
    ad_hoc: boolean
    events: WorkEventInline[]
}

/**
 * Employee data with their workdays (which include inline events),
 * assembled client-side.
 */
export interface EmployeeWithWorkdays extends Employee {
    workdays: WorkDayWithEvents[]
    total_time?: string
}


export interface EmployeeResponse {
    results: Employee[],
    count: number,
    __source: "online" | "offline"
}

export interface EmployeeWithWorkdaysResponse {
    results: EmployeeWithWorkdays[],
    count: number,
    __source: "online" | "offline"
}

export interface WorkdayResponse {
    results: WorkdayWithEmployee[] | WorkDay[],
    count: number,
    __source: "online" | "offline"
}

interface EmployeesStore {
    employees?: Employee[];
    getById: (id: number) => Employee | undefined;

    setEmployees(employees: Employee[]): void;

    refetch?: () => void
    addRefetchAction: (ref: any) => void
}

export const useEmployeesStore = create<EmployeesStore>((set, get) => ({
    employees: [],
    setEmployees: (employees: Employee[]) => set({employees}),
    addRefetchAction: (ref: any) => set({refetch: ref}),
    getById: (id: number) => {
        return get().employees?.find(e => e.id === id)
    }
}))


export const combineEmployeeWorkdaysEventsData = (
    employees: Employee[],
    workdays: WorkdayWithEmployee[],
    events: CommonEventsList,
): EmployeeWithWorkdays[] => {
    const workDaysMap = new Map<number, WorkdayWithEmployee[]>();
    for (const workday of workdays) {
        const emp = workday.employee;
        const wdMap = workDaysMap.get(emp.id)
        if (wdMap) wdMap.push(workday); else workDaysMap.set(emp.id, [workday]);
    }

    const eventsByEmpDate = new Map<string, WorkEventInline[]>();
    for (const ev of events) {
        //@ts-ignore
        const empId = "event_employee" in ev ? ev?.event_employee : ev?.workday?.employee?.id;
        if (!empId) continue;
        const day = ev.recorded_at.slice(0, 10);
        const key = `${empId}|${day}`;
        const arr = eventsByEmpDate.get(key);
        //@ts-ignore
        if (arr) arr.push(ev); else eventsByEmpDate.set(key, [ev]);
    }

    return employees.map(emp => {
        const days = workDaysMap.get(emp.id) ?? [];
        const withEvents: WorkDayWithEvents[] = days.map(wd => ({
            id: wd.id,
            date: wd.date,
            start_time: wd.start_time,
            end_time: wd.end_time,
            status: wd.status as unknown as StatusEnum,
            total_time: wd.total_time,
            inconsistent: wd.inconsistent,
            ad_hoc: wd.ad_hoc,
            events: eventsByEmpDate.get(`${emp.id}|${wd.date}`) ?? [],
        }));
        return {...emp, workdays: withEvents};
    });
}
